import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, Typography, alpha } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';

const ConfirmContext = createContext(null);

/**
 * Diálogo de confirmación reutilizable con API de promesa:
 *   const confirmar = useConfirm();
 *   if (await confirmar({ titulo, descripcion, textoConfirmar: 'Eliminar', tono: 'peligro' })) { ... }
 */
export const ConfirmProvider = ({ children }) => {
  const [estado, setEstado] = useState({ open: false, opciones: {} });
  const resolver = useRef(null);

  const confirmar = useCallback(
    (opciones = {}) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        setEstado({ open: true, opciones });
      }),
    []
  );

  const cerrar = (valor) => {
    setEstado((prev) => ({ ...prev, open: false }));
    resolver.current?.(valor);
    resolver.current = null;
  };

  const {
    titulo = '¿Confirmar acción?',
    descripcion,
    textoConfirmar = 'Confirmar',
    textoCancelar = 'Cancelar',
    tono = 'normal',
  } = estado.opciones;
  const peligro = tono === 'peligro';
  const Icono = peligro ? WarningAmberRoundedIcon : HelpOutlineRoundedIcon;

  return (
    <ConfirmContext.Provider value={confirmar}>
      {children}
      <Dialog
        open={estado.open}
        onClose={() => cerrar(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="confirm-titulo"
      >
        <Box sx={{ display: 'flex', gap: 2, p: 3, pb: 1.5 }}>
          <Box
            sx={{
              width: 44, height: 44, flexShrink: 0, borderRadius: '50%',
              display: 'grid', placeItems: 'center',
              bgcolor: (t) => alpha(peligro ? t.palette.error.main : t.palette.primary.main, 0.12),
              color: peligro ? 'error.main' : 'primary.main',
            }}
          >
            <Icono />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography id="confirm-titulo" variant="h6">
              {titulo}
            </Typography>
            {descripcion && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {descripcion}
              </Typography>
            )}
          </Box>
        </Box>
        <DialogActions>
          <Button onClick={() => cerrar(false)} color="inherit">
            {textoCancelar}
          </Button>
          <Button
            onClick={() => cerrar(true)}
            variant="contained"
            color={peligro ? 'error' : 'primary'}
            autoFocus
          >
            {textoConfirmar}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>');
  return ctx;
};
